/**
 * Connects a browser interface to the session's realtime transport.
 *
 * §19.4 — the same interfaces run on both deployment profiles. Which transport
 * is in use comes from the session snapshot, not from a build flag.
 */

import { useEffect, useRef, useState } from "react";
import type { EventEnvelope, EventType } from "@tq/protocol";
import {
  AblyClient,
  LocalWebSocketClient,
  type ConnectionState,
  type RealtimeClient,
} from "@tq/realtime/client";
import type { SessionSnapshot } from "./api.js";

export interface RealtimeHandle {
  state: ConnectionState;
  /** Server-minus-client clock skew, applied to every countdown (§21.7). */
  clockOffsetMs: number;
}

export function useRealtime(
  snapshot: SessionSnapshot | null,
  onEvent: (event: EventEnvelope) => void,
): RealtimeHandle {
  const [state, setState] = useState<ConnectionState>("disconnected");
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const clientRef = useRef<RealtimeClient | null>(null);
  // Held in a ref so a re-render of the consumer does not tear down the socket.
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!snapshot) return;

    // The snapshot's serverNow is read at the moment it arrives, so the skew
    // includes at most one round trip rather than accumulating.
    setClockOffsetMs(snapshot.serverNow - Date.now());

    const client: RealtimeClient =
      snapshot.realtime.transport === "local_ws"
        ? new LocalWebSocketClient(snapshot.realtime.url, snapshot.realtime.channels)
        : new AblyClient({
            tokenUrl: snapshot.realtime.url,
            channels: snapshot.realtime.channels,
            clientId: snapshot.realtime.clientId,
          });

    clientRef.current = client;
    const offState = client.onStateChange(setState);
    const offEvent = client.on("*", (event) => handlerRef.current(event));

    void client.connect().catch(() => setState("disconnected"));

    return () => {
      offState();
      offEvent();
      client.disconnect();
      clientRef.current = null;
    };
    // Reconnect when the session, its transport, or the channel list changes.
    // The channel list matters: a player's private channel only appears in the
    // snapshot once they have a seat in the session, and a socket opened before
    // that would stay subscribed to the public channel alone — which is how a
    // device ends up connected and yet never handed a question.
  }, [
    snapshot?.sessionId,
    snapshot?.realtime.transport,
    snapshot?.realtime.url,
    snapshot?.realtime.channels.join(","),
  ]);

  return { state, clockOffsetMs };
}

/** Narrows an envelope to one event type, for a typed handler body. */
export function isEvent<T extends EventType>(
  event: EventEnvelope,
  type: T,
): event is EventEnvelope<T> {
  return event.type === type;
}
